import { useState } from 'react'
import { useAppSelector } from './reduxToolkit'
import { useSubscriptionAccess } from './useSubscriptionAccess'
import { SubscriptionModal } from '../components/subscriptionModal'
import { UpgradePlanModal } from '../components/upgradePlanModal'
import { isFamilyLimitedTier } from '../../lib/rules/requiredPlan'

export interface SubscriptionPromptConfig {
  /**
   * Trigger condition - when should the modal be shown?
   * - 'second-incident': Show when user tries to create a second real incident
   * - 'always': Show every time if no subscription
   * - 'custom': Use the shouldPrompt callback
   */
  trigger: 'second-incident' | 'always' | 'custom'

  /**
   * Custom condition function (only used when trigger is 'custom')
   */
  shouldPrompt?: () => boolean

  /**
   * When the user HAS a subscription: returns true if the attempted action
   * requires the Family plan (submitting for a second person, inviting a
   * spouse). Solo-tier subscribers are shown the upgrade modal; every other
   * tier (family, legacy basic/premium) passes — see
   * lib/rules/requiredPlan.ts. Also used to preselect the Family plan in the
   * subscribe modal for users with no subscription yet.
   */
  requiresFamilyPlan?: () => boolean

  /**
   * Modal title
   */
  title?: string

  /**
   * Modal message
   */
  message?: string

  /**
   * Title for the Solo → Family upgrade modal
   */
  upgradeTitle?: string

  /**
   * Message for the Solo → Family upgrade modal
   */
  upgradeMessage?: string

  /**
   * Success URL after subscribing
   */
  successUrl?: string

  /**
   * Cancel URL if user cancels subscription
   */
  cancelUrl?: string
}

const DEFAULT_CONFIG: Partial<SubscriptionPromptConfig> = {
  title: 'Subscribe to Continue',
  message: 'Get full access to all features with a subscription.',
  successUrl: window.location.origin + '/#/subscribe/success',
  cancelUrl: window.location.href,
}

/**
 * Hook to manage subscription prompts throughout the app.
 * Returns a function to check if subscription is needed, and a modal component to render.
 *
 * @example
 * const { checkSubscription, SubscriptionPromptModal } = useSubscriptionPrompt({
 *   trigger: 'second-incident',
 *   title: 'Subscribe for Unlimited Incidents',
 *   message: 'Subscribe to create unlimited incidents'
 * })
 *
 * const handleClick = () => {
 *   if (!checkSubscription()) {
 *     return // Subscription required, modal will be shown
 *   }
 *   // Continue with action
 * }
 *
 * return <>
 *   <button onClick={handleClick}>Do Something</button>
 *   <SubscriptionPromptModal />
 * </>
 */
export function useSubscriptionPrompt(config: SubscriptionPromptConfig) {
  const [modalMode, setModalMode] = useState<'subscribe' | 'upgrade' | null>(null)
  const { hasOrganization, hasSubscription } = useSubscriptionAccess()
  const subscriptionTier = useAppSelector((s) => s.membership?.subscription?.tier)

  // Count real incidents (excluding demo incident from tour)
  const realIncidentCount = useAppSelector((state) =>
    state.incidents.incidents.filter(
      incident => incident.description !== 'Broken Leg (demo incident)'
    ).length
  )

  const mergedConfig = { ...DEFAULT_CONFIG, ...config }

  /**
   * Check if user needs to subscribe (or upgrade) based on the configured trigger.
   * Returns true if user can proceed, false if a subscription or upgrade is
   * required (and the corresponding modal will be shown)
   */
  const checkSubscription = (): boolean => {
    // Organization users are billed through the org and never gated here
    if (hasOrganization) {
      return true
    }

    if (hasSubscription) {
      // Solo subscribers must upgrade to Family for family-scoped actions.
      // Any other tier passes (family, plus grandfathered legacy tiers).
      if (isFamilyLimitedTier(subscriptionTier) && config.requiresFamilyPlan?.()) {
        setModalMode('upgrade')
        return false
      }
      return true
    }

    // No subscription: check trigger condition
    let shouldBlock = false

    if (config.trigger === 'second-incident') {
      shouldBlock = realIncidentCount >= 1
    } else if (config.trigger === 'always') {
      shouldBlock = true
    } else if (config.trigger === 'custom' && config.shouldPrompt) {
      shouldBlock = config.shouldPrompt()
    }

    if (shouldBlock) {
      setModalMode('subscribe')
      return false
    }

    return true
  }

  /**
   * Modal component to render - handles its own show/hide state
   */
  const SubscriptionPromptModal = () => (
    <>
      <SubscriptionModal
        show={modalMode === 'subscribe'}
        onHide={() => setModalMode(null)}
        title={mergedConfig.title!}
        message={mergedConfig.message!}
        successUrl={mergedConfig.successUrl!}
        cancelUrl={mergedConfig.cancelUrl!}
        preselectTier={config.requiresFamilyPlan?.() ? 'family' : undefined}
      />
      <UpgradePlanModal
        show={modalMode === 'upgrade'}
        onHide={() => setModalMode(null)}
        title={mergedConfig.upgradeTitle}
        message={mergedConfig.upgradeMessage}
      />
    </>
  )

  return {
    checkSubscription,
    SubscriptionPromptModal,
    hasSubscription,
    hasOrganization,
  }
}
